import React, { useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  Plus,
  MessageSquare,
  MessageCircle,
  Menu,
  X,
  LogOut,
  Users,
  Hotel,
  LayoutDashboard,
  LineChart,
  Crown,
  MapPin,
  FileText,
  CalendarCheck,
  Image as ImageIcon,
  Calendar,
  Receipt,
  Tag,
  Sparkles,
  Star,
  UserCog,
} from 'lucide-react';
import { useQueryCount } from "../hooks/useQueryCount";
import { useAdminQueryCount } from '../hooks/useAdminQueryCount';

const SCROLL_KEY = 'adminSidebarScrollTop';

function useSidebarScrollPersistence(ref) {
  // Restore on mount
  useEffect(() => {
    const node = ref.current;
    if (!node) return;
    const saved = parseInt(sessionStorage.getItem(SCROLL_KEY) || '0', 10);
    if (!Number.isNaN(saved)) {
      node.scrollTop = saved;
    }
    const onScroll = () => {
      sessionStorage.setItem(SCROLL_KEY, String(node.scrollTop));
    };
    node.addEventListener('scroll', onScroll, { passive: true });
    return () => node.removeEventListener('scroll', onScroll);
  }, [ref]);
}

export function AdminLayout({ children, user, onLogout }) {
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const location = useLocation();
  const { newQueriesCount } = useQueryCount();
  const { newQueryCount } = useAdminQueryCount();
  const desktopNavRef = useRef(null);
  const mobileNavRef = useRef(null);

  useSidebarScrollPersistence(desktopNavRef);
  useSidebarScrollPersistence(mobileNavRef);

  const menuSections = [
    {
      heading: 'Overview',
      items: [
        { path: '/admin', icon: LayoutDashboard, label: 'Dashboard' },
        { path: '/admin/analytics', icon: LineChart, label: 'Analytics' },
      ],
    },
    {
      heading: 'Villas',
      items: [
        { path: '/admin/hotels', icon: Hotel, label: 'Manage Villas' },
        { path: '/add-property', icon: Plus, label: 'Add Villa' },
        { path: '/admin/villa-images', icon: ImageIcon, label: 'Villa Images' },
        { path: '/admin/availability', icon: CalendarCheck, label: 'Availability' },
        { path: '/admin/luxury-villas', icon: Crown, label: 'Luxury Villas' },
        { path: '/admin/promoted-villas', icon: Star, label: 'Promoted Villas' },
      ],
    },
    {
      heading: 'Operations',
      items: [
        { path: '/admin/bookings', icon: Calendar, label: 'Bookings' },
        { path: '/admin/billing', icon: Receipt, label: 'Billing' },
        { path: '/admin/offers-discounts', icon: Tag, label: 'Offers & Discounts' },
        { path: '/admin/special-occasions', icon: Sparkles, label: 'Special Occasions' },
      ],
    },
    {
      heading: 'People & Content',
      items: [
        { path: '/admin/users', icon: Users, label: 'Property Owners' },
        { path: '/admin/admins', icon: UserCog, label: 'Admins' },
        {
          path: '/admin/queries',
          icon: MessageSquare,
          label: 'Admin Queries',
          badge: newQueryCount,
        },
        {
          path: '/admin/users-queries',
          icon: MessageCircle,
          label: 'User Queries',
          badge: newQueriesCount,
        },
        { path: '/admin/destinations', icon: MapPin, label: 'Destinations' },
        { path: '/admin/create-blog', icon: FileText, label: 'Create Blog' },
      ],
    },
  ];

  const isActive = (path) => location.pathname === path;

  const NavItem = ({ item, onClick }) => (
    <Link
      to={item.path}
      onClick={onClick}
      className={`flex items-center justify-between gap-3 px-3 py-2 rounded-md text-sm ${
        isActive(item.path)
          ? 'bg-black text-white'
          : 'text-gray-700 hover:bg-gray-100'
      }`}
    >
      <div className="flex items-center gap-3 min-w-0">
        <item.icon className="h-4 w-4 shrink-0" />
        <span className="truncate">{item.label}</span>
      </div>
      {item.badge > 0 && (
        <span
          className={`text-[11px] font-medium rounded-full px-1.5 py-0.5 min-w-[20px] text-center ${
            isActive(item.path) ? 'bg-white text-black' : 'bg-black text-white'
          }`}
        >
          {item.badge > 99 ? '99+' : item.badge}
        </span>
      )}
    </Link>
  );

  const SidebarContent = ({ navRef, onItemClick }) => (
    <>
      <div className="px-5 py-5 border-b border-gray-200">
        <Link to="/admin" onClick={onItemClick} className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-md bg-black text-white flex items-center justify-center font-bold text-sm">
            V
          </div>
          <div>
            <div className="text-sm font-semibold text-gray-900">VillaKosh</div>
            <div className="text-[11px] text-gray-500">Admin Panel</div>
          </div>
        </Link>
      </div>

      <nav ref={navRef} className="flex-1 overflow-y-auto px-3 py-4 space-y-5">
        {menuSections.map((section) => (
          <div key={section.heading}>
            <div className="px-3 mb-1.5 text-[11px] font-medium uppercase tracking-wider text-gray-400">
              {section.heading}
            </div>
            <div className="space-y-0.5">
              {section.items.map((item) => (
                <NavItem key={item.path} item={item} onClick={onItemClick} />
              ))}
            </div>
          </div>
        ))}
      </nav>

      <div className="border-t border-gray-200 p-3">
        {user && (
          <div className="flex items-center gap-3 px-2 py-2 mb-1">
            <img
              src={user.photoURL || '/placeholder.svg'}
              alt={user.displayName || 'Admin'}
              className="w-8 h-8 rounded-full object-cover bg-gray-200"
            />
            <div className="min-w-0 flex-1">
              <div className="text-sm font-medium text-gray-900 truncate">
                {user.displayName || 'Admin'}
              </div>
              <div className="text-[11px] text-gray-500 truncate">{user.email}</div>
            </div>
          </div>
        )}
        <button
          onClick={onLogout}
          className="flex items-center gap-3 px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-md w-full"
        >
          <LogOut className="h-4 w-4" />
          <span>Logout</span>
        </button>
      </div>
    </>
  );

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Desktop Sidebar */}
      <aside className="fixed left-0 top-0 bottom-0 w-64 bg-white border-r border-gray-200 hidden lg:flex lg:flex-col">
        <SidebarContent navRef={desktopNavRef} />
      </aside>

      {/* Mobile Top Bar */}
      <header className="lg:hidden sticky top-0 z-30 bg-white border-b border-gray-200">
        <div className="flex items-center justify-between px-4 h-14">
          <button
            type="button"
            onClick={() => setShowMobileMenu(true)}
            className="p-2 -ml-2 text-gray-700 hover:bg-gray-100 rounded-md"
            aria-label="Open menu"
          >
            <Menu className="h-5 w-5" />
          </button>
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 rounded-md bg-black text-white flex items-center justify-center font-bold text-xs">
              V
            </div>
            <span className="text-sm font-semibold text-gray-900">Admin</span>
          </div>
          {user ? (
            <img
              src={user.photoURL || '/placeholder.svg'}
              alt={user.displayName || 'Admin'}
              className="w-8 h-8 rounded-full object-cover bg-gray-200"
            />
          ) : (
            <div className="w-8 h-8" />
          )}
        </div>
      </header>

      {/* Mobile Drawer */}
      {showMobileMenu && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div
            className="fixed inset-0 bg-black/40"
            onClick={() => setShowMobileMenu(false)}
          />
          <div className="fixed left-0 top-0 bottom-0 w-72 bg-white flex flex-col">
            <div className="flex items-center justify-end px-3 pt-3">
              <button
                type="button"
                onClick={() => setShowMobileMenu(false)}
                className="p-2 text-gray-700 hover:bg-gray-100 rounded-md"
                aria-label="Close menu"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <SidebarContent
              navRef={mobileNavRef}
              onItemClick={() => setShowMobileMenu(false)}
            />
          </div>
        </div>
      )}

      {/* Main Content */}
      <main className="lg:ml-64 min-h-screen">
        <div className="max-w-6xl mx-auto p-4 lg:p-8">{children}</div>
      </main>
    </div>
  );
}
